import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Lock, AlertTriangle } from 'lucide-react';
import { getCapacitaCalendario } from '../../api/programmazione';
import type { GiornoCalendario } from '../../api/programmazione';

interface Props {
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}

function toISO(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function satColor(pct: number, isChiuso: boolean): string {
  if (isChiuso) return '#e5e7eb';
  if (pct >= 100) return '#fca5a5';
  if (pct >= 80) return '#fee2e2';
  if (pct >= 50) return '#fef3c7';
  return '#dcfce7';
}

export default function MiniCalendarioSaturazione({ selectedDate, onSelectDate }: Props) {
  const { t } = useTranslation();
  const WEEK_DAYS = t('common.calendario.week_days', { returnObjects: true }) as string[];
  const MONTH_NAMES = t('common.calendario.month_names', { returnObjects: true }) as string[];
  const today = new Date();
  const todayISO = toISO(today.getFullYear(), today.getMonth(), today.getDate());
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [giorni, setGiorni] = useState<Record<string, GiornoCalendario>>({});

  const loadData = useCallback(() => {
    void (async () => {
      const firstDay = toISO(year, month, 1);
      const lastDayNum = new Date(year, month + 1, 0).getDate();
      const lastDay = toISO(year, month, lastDayNum);
      try {
        const res = await getCapacitaCalendario(firstDay, lastDay);
        const map: Record<string, GiornoCalendario> = {};
        for (const g of res.giorni) {
          map[g.data] = g;
        }
        setGiorni(map);
      } catch {
        // silently fail
      }
    })();
  }, [year, month]);

  useEffect(() => { loadData(); }, [loadData]);

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }

  const firstWeekday = new Date(year, month, 1).getDay();
  // Converti domenica=0 → 6, lunedì=1 → 0
  const startOffset = (firstWeekday === 0 ? 6 : firstWeekday - 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-white border border-stone-mid rounded-xl p-3 select-none">
      {/* Header navigazione */}
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={prevMonth} className="p-1 text-ink-mid hover:text-ink-dark rounded">
          <ChevronLeft size={14} />
        </button>
        <span className="text-xs font-semibold text-ink-dark">
          {MONTH_NAMES[month]} {year}
        </span>
        <button type="button" onClick={nextMonth} className="p-1 text-ink-mid hover:text-ink-dark rounded">
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Intestazione giorni */}
      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {WEEK_DAYS.map(d => (
          <div key={d} className="text-center text-[10px] font-medium text-ink-faint py-0.5">{d}</div>
        ))}
      </div>

      {/* Griglia giorni */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => {
          if (day === null) return <div key={`e-${i}`} />;

          const iso = toISO(year, month, day);
          const g = giorni[iso];
          const isWeekend = new Date(year, month, day).getDay() === 0 || new Date(year, month, day).getDay() === 6;
          const isChiuso = g?.impianto_chiuso || false;
          const isRicezioneChiusa = g?.ricezione_chiusa || false;
          // A1: non si programma nel passato; oggi è evidenziato.
          const isPast = iso < todayISO;
          const isToday = iso === todayISO;
          const selezionabile = !isWeekend && !isChiuso && !isRicezioneChiusa && !isPast;
          const isSelected = iso === selectedDate;
          const pct = g?.saturazione_pct ?? 0;
          const bg = isPast || isWeekend ? '#f3f4f6' : satColor(pct, isChiuso);

          return (
            <button
              key={iso}
              type="button"
              disabled={!selezionabile}
              onClick={() => selezionabile && onSelectDate(iso)}
              title={g?.note_chiusura ?? undefined}
              className={`relative flex flex-col items-center justify-start py-0.5 rounded text-[11px] font-medium transition-all
                ${selezionabile ? 'cursor-pointer hover:ring-1 hover:ring-sage hover:ring-offset-0' : 'cursor-not-allowed'}
                ${isPast ? 'opacity-40 text-ink-faint' : !selezionabile ? 'opacity-50' : ''}
                ${isSelected ? 'ring-2 ring-sage ring-offset-0' : isToday ? 'ring-2 ring-amber-400 ring-offset-0 font-bold' : ''}
              `}
              style={{ background: bg, minHeight: 28 }}
            >
              <span className="leading-none">{day}</span>
              {isChiuso && <Lock size={8} className="text-ink-mid mt-0.5" />}
              {isRicezioneChiusa && !isChiuso && <AlertTriangle size={8} className="text-amber-500 mt-0.5" />}
              {!isWeekend && !isChiuso && pct > 0 && (
                <span className="text-[8px] leading-none text-ink-mid">{pct}%</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {[
          { color: '#dcfce7', label: '<50%' },
          { color: '#fef3c7', label: '50–79%' },
          { color: '#fee2e2', label: '80–99%' },
          { color: '#fca5a5', label: '≥100%' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm border border-stone-border" style={{ background: l.color }} />
            <span className="text-[9px] text-ink-faint">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
